import { getAssetCanvasNodeDimensions } from '@/lib/canvas/asset-canvas-node-size';

export const CONVERSATION_HUB_BODY_MIN_HEIGHT = getAssetCanvasNodeDimensions('16 / 9').height;
export const CONVERSATION_HUB_BODY_MAX_HEIGHT = CONVERSATION_HUB_BODY_MIN_HEIGHT * 2;
/** 底边拉伸热区高度；内容区需留出同等 padding，避免挡住滚动末行。 */
export const CONVERSATION_HUB_RESIZE_HANDLE_HEIGHT = 8;

type ConversationHubHeightNode = {
  id: string;
  data: Record<string, unknown>;
};

function readProjectionKind(data: Record<string, unknown>): string | undefined {
  const projection = data.projection;
  if (!projection || typeof projection !== 'object') return undefined;
  const kind = (projection as { kind?: unknown }).kind;
  return typeof kind === 'string' ? kind : undefined;
}

export function clampConversationHubBodyHeight(value: number): number {
  if (!Number.isFinite(value)) return CONVERSATION_HUB_BODY_MIN_HEIGHT;
  return Math.round(
    Math.min(CONVERSATION_HUB_BODY_MAX_HEIGHT, Math.max(CONVERSATION_HUB_BODY_MIN_HEIGHT, value))
  );
}

export function inheritConversationHubBodyHeight<T extends ConversationHubHeightNode>(
  synced: T,
  current: T | undefined
): T {
  if (!current) return synced;
  if (readProjectionKind(synced.data) !== 'conversation') return synced;
  const height = current.data.conversationBodyHeight;
  if (typeof height !== 'number' || !Number.isFinite(height)) return synced;
  const nextHeight = clampConversationHubBodyHeight(height);
  if (synced.data.conversationBodyHeight === nextHeight) return synced;
  return {
    ...synced,
    data: {
      ...synced.data,
      conversationBodyHeight: nextHeight,
    },
  };
}

export function inheritConversationHubBodyHeights<T extends ConversationHubHeightNode>(
  syncedNodes: readonly T[],
  currentNodes: readonly T[]
): T[] {
  if (currentNodes.length === 0) return syncedNodes as T[];
  const currentById = new Map(currentNodes.map((node) => [node.id, node]));
  let changed = false;
  const next = syncedNodes.map((node) => {
    const inherited = inheritConversationHubBodyHeight(node, currentById.get(node.id));
    if (inherited !== node) changed = true;
    return inherited;
  });
  return changed ? next : (syncedNodes as T[]);
}

export function patchConversationHubBodyHeight<T extends ConversationHubHeightNode>(
  nodes: readonly T[],
  nodeId: string,
  height: number
): T[] {
  const nextHeight = clampConversationHubBodyHeight(height);
  let changed = false;
  const next = nodes.map((node) => {
    if (node.id !== nodeId) return node;
    if (node.data.conversationBodyHeight === nextHeight) return node;
    changed = true;
    return {
      ...node,
      data: {
        ...node.data,
        conversationBodyHeight: nextHeight,
      },
    };
  });
  return changed ? next : (nodes as T[]);
}
